/**
 * 住宅ローン控除（住宅借入金等特別控除）の計算（#63）。
 *
 * 住宅借入金等特別控除は所得控除ではなく**税額控除**である。所得税額から控除し、
 * 引ききれない額は翌年度の住民税所得割から控除する（上限あり）。
 *
 * 出典:
 * - 国税庁 No.1211-1 住宅の新築等をし、令和4年以降に居住の用に供した場合
 * - 国税庁 No.1211-3 中古住宅を取得し、令和4年以降に居住の用に供した場合
 * - 総務省・自治体（横浜市「税額控除(令和8年度課税以降）」）の住民税の控除限度額
 *
 * 計算は純粋関数で行い、UI・I/O に依存しない。金額は円単位の整数。
 */

import type { FamilyData, TaxRules } from './types';

/** 住宅ローン控除の控除額を計算するための入力（計算本体と明細で共有する）。 */
export interface HousingLoanContext {
  /** 所得税額（税額控除前）。 */
  incomeTax: number;
  /** 所得税の課税総所得金額等（所得税の課税所得）。住民税分の限度額の基礎に用いる。 */
  incomeTaxableIncome: number;
  /** 適用されている所得税の基礎控除額（住民税分の限度額の調整に用いる）。 */
  incomeTaxBasicDeduction: number;
  /** 本人の合計所得金額（円）。適用要件（2,000万円以下）の判定に用いる。 */
  totalIncome: number;
  /** 調整控除後の住民税所得割額（円）。住民税分の控除の上限に用いる。 */
  residentIncomeLevy: number;
  rules: TaxRules;
}

/** 住宅ローン控除の計算結果。 */
export interface HousingLoanResult {
  /** 適用要件を満たし控除可能額があるか。 */
  applicable: boolean;
  /** 居住開始年（西暦。未入力は 0）。 */
  moveInYear: number;
  /** 控除率（0.7% など）。 */
  rate: number;
  /** 控除対象借入限度額（円）。 */
  borrowingLimit: number;
  /** 適用した年末残高（円）。 */
  yearEndBalance: number;
  /** 住宅借入金等特別控除可能額（円）。 */
  availableCredit: number;
  /** 所得税から控除した額（円）。 */
  incomeTaxCredit: number;
  /** 住民税所得割から控除した額（円）。 */
  residentTaxCredit: number;
  /** 住民税分の控除限度額（円）。 */
  residentCreditLimit: number;
  /** 住民税分の控除限度額の上限（97,500 / 136,500 円）。 */
  residentCreditLimitCap: number;
  /** 適用しない・近似した場合の補足（該当なしは空文字）。 */
  note: string;
}

function emptyResult(note: string): HousingLoanResult {
  return {
    applicable: false,
    moveInYear: 0,
    rate: 0,
    borrowingLimit: 0,
    yearEndBalance: 0,
    availableCredit: 0,
    incomeTaxCredit: 0,
    residentTaxCredit: 0,
    residentCreditLimit: 0,
    residentCreditLimitCap: 0,
    note,
  };
}

/** 控除額の 100 円未満切捨て（国税庁 No.1211-1）。浮動小数を避け整数演算で行う。 */
function housingLoanCreditAmount(base: number, ratePermille: number): number {
  return Math.floor((base * ratePermille) / 1000 / 100) * 100;
}

/**
 * 住宅ローン控除の可能額を所得税・住民税で配分する。
 *
 * - 所得税分 = min(可能額, 所得税額)
 * - 住民税分 = min(可能額 − 所得税分, 住民税分の限度額, 調整控除後の所得割額)
 */
export function splitHousingLoanCredit(
  availableCredit: number,
  incomeTax: number,
  residentCreditLimit: number,
  residentIncomeLevy: number,
): { incomeTaxCredit: number; residentTaxCredit: number } {
  const credit = Math.max(0, Math.floor(availableCredit));
  const incomeTaxCredit = Math.min(credit, Math.max(0, incomeTax));
  const remaining = credit - incomeTaxCredit;
  const residentTaxCredit = Math.min(
    remaining,
    Math.max(0, residentCreditLimit),
    Math.max(0, residentIncomeLevy),
  );
  return { incomeTaxCredit, residentTaxCredit };
}

/**
 * 住宅ローン控除を計算する。居住年・取得区分・年末残高等から可能額を求め、
 * 所得税・住民税への配分を返す。
 */
export function calculateHousingLoan(
  familyData: FamilyData | null,
  context: HousingLoanContext,
): HousingLoanResult {
  if (!familyData) {
    return emptyResult('');
  }
  const {
    housingMoveInYear,
    housingMoveInMonth,
    housingAcquisition,
    housingCategory,
    housingChildRearing,
    housingSpecialAcquisition,
    housingYearEndBalance,
    housingCreditPossibleAmount,
    housingTotalIncomeMan,
  } = familyData;

  if (housingMoveInYear <= 0) {
    return emptyResult('');
  }
  const balance = Math.max(0, housingYearEndBalance);
  const possibleAmount = Math.max(0, housingCreditPossibleAmount);
  if (balance <= 0 && possibleAmount <= 0) {
    return emptyResult('');
  }

  const { rules } = context;
  const supported = rules.housingLoanSupportedMoveInYears();
  if (housingMoveInYear < supported.min || housingMoveInYear > supported.max) {
    return emptyResult(
      `居住年 ${housingMoveInYear} 年は対応範囲（${supported.min}〜${supported.max}年）外のため、住宅ローン控除は計算していません。`,
    );
  }

  const totalIncome =
    housingTotalIncomeMan > 0 ? housingTotalIncomeMan * 10000 : context.totalIncome;
  if (totalIncome > rules.housingLoanIncomeRequirement()) {
    return emptyResult('合計所得金額が2,000万円を超えるため、住宅ローン控除の適用要件を満たしません。');
  }

  const rate = rules.housingLoanRate();
  const borrowingLimit = rules.housingLoanBorrowingLimit(
    housingMoveInYear,
    housingAcquisition,
    housingCategory,
    housingChildRearing,
  );
  if (borrowingLimit <= 0 && possibleAmount <= 0) {
    return emptyResult(
      '選択した住宅区分・居住年では住宅ローン控除の対象となりません（省エネ基準を満たさない新築等など）。',
    );
  }

  const availableCredit =
    possibleAmount > 0
      ? possibleAmount
      : housingLoanCreditAmount(Math.min(balance, borrowingLimit), Math.round(rate * 1000));

  const residentCreditLimitCap = rules.housingLoanResidentCreditLimitCap(
    housingMoveInYear,
    housingMoveInMonth,
    housingSpecialAcquisition,
  );
  const residentCreditLimitRate = rules.housingLoanResidentCreditLimitRate(
    housingMoveInYear,
    housingMoveInMonth,
    housingSpecialAcquisition,
  );
  const residentCreditLimitBase =
    context.incomeTaxableIncome +
    rules.housingLoanResidentCreditBaseAdjustment(context.incomeTaxBasicDeduction);
  const residentCreditLimit = Math.min(
    Math.floor(Math.max(0, residentCreditLimitBase) * residentCreditLimitRate),
    residentCreditLimitCap,
  );

  const { incomeTaxCredit, residentTaxCredit } = splitHousingLoanCredit(
    availableCredit,
    context.incomeTax,
    residentCreditLimit,
    context.residentIncomeLevy,
  );

  return {
    applicable: true,
    moveInYear: housingMoveInYear,
    rate,
    borrowingLimit,
    yearEndBalance: balance,
    availableCredit,
    incomeTaxCredit,
    residentTaxCredit,
    residentCreditLimit,
    residentCreditLimitCap,
    note: '',
  };
}
